import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CalendarClock, AlertTriangle } from "lucide-react";

const CELL = {
  SNAPSHOT_COMPLETE: { c: "bg-emerald-500", t: "Complete" },
  SNAPSHOT_MISSED: { c: "bg-rose-500", t: "Missed" },
  COLLECTOR_FAILED: { c: "bg-orange-500", t: "Collector failed" },
  SUSPENDED_SAFETY_STOP: { c: "bg-amber-500", t: "Suspended (safety stop)" },
  SKIPPED_ALREADY_COMPLETED: { c: "bg-emerald-700", t: "Skipped (already complete)" },
  PENDING_TODAY: { c: "bg-sky-600", t: "Pending today" },
  NO_COLLECTION: { c: "bg-[#27272A]", t: "No collection" },
};

export default function SnapshotHealth() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/amazon/snapshot-health", { params: { days: 14 } }).then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;

  const missed = (d.summary?.SNAPSHOT_MISSED || 0) + (d.summary?.COLLECTOR_FAILED || 0) + (d.summary?.SUSPENDED_SAFETY_STOP || 0);
  return (
    <div className="p-8 max-w-[1100px]" data-testid="snapshot-health-page">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="text-emerald-400" size={20} />
        <h1 className="font-head text-2xl font-bold tracking-tight">Snapshot Health</h1>
      </div>
      <p className="text-xs font-mono text-[#71717A] mb-6 max-w-3xl">
        Daily Amazon NR collection status per category. A missing snapshot stays <span className="text-rose-300">missing evidence</span> —
        ranks are never interpolated. Scheduler {d.scheduler_enabled ? <span className="text-emerald-300">enabled</span> : <span className="text-rose-300">disabled</span>} ·
        runs daily at {String(d.snapshot_hour_utc).padStart(2, "0")}:00 UTC · {d.scheduler_version}
      </p>

      {missed > 0 && (
        <div data-testid="missed-day-alert" className="flex items-center gap-2 border border-rose-800 bg-rose-950/40 text-rose-300 px-4 py-2.5 mb-6 font-mono text-xs">
          <AlertTriangle size={14} />
          <strong>{missed}</strong> snapshot integrity issue(s) in the last {d.days} days (missed / failed / suspended). Missing days are evidence gaps.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6 text-[10px] font-mono">
        {Object.entries(CELL).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5 text-[#71717A]">
            <span className={`w-3 h-3 ${v.c}`} /> {v.t}
          </span>
        ))}
      </div>

      <div className="space-y-4">
        {d.categories.map((cat) => (
          <div key={cat.category_key} data-testid={`health-cat-${cat.category_key}`} className="border border-[#27272A] bg-[#18181B] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[#F4F4F5] font-medium">{cat.category}</span>
              <span className="text-[10px] font-mono text-[#71717A]">first collection: {cat.first_collection || "—"}</span>
            </div>
            <div className="flex gap-1 flex-wrap">
              {cat.timeline.map((day) => {
                const cell = CELL[day.status] || CELL.NO_COLLECTION;
                return (
                  <div key={day.date} data-testid={`health-cell-${cat.category_key}-${day.date}`} title={`${day.date} · ${cell.t}`}
                    className="flex flex-col items-center gap-1">
                    <span className={`w-7 h-7 ${cell.c} border border-black/20`} />
                    <span className="text-[8px] font-mono text-[#52525B]">{day.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
